import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import Afinador from "./components/Afinador";
import Setlist from "./components/Setlist";
import AtividadeItem from "./components/AtividadeItem";
import NovoEvento from "./components/NovoEvento";
import VideoEnsino from "./components/VideoEnsino";
import EditarCifra from "./components/EditarCifra";
import Player from "./components/Player";
import Navbar from "./components/Navbar";
import LogoAdoraTeam from "./components/LogoAdoraTeam";
import SplashScreen from "./components/SplashScreen";
import Home from "./components/Home";
import Escala from "./components/Escala";
import RedeSocial from "./components/RedeSocial";
import Perfil from "./components/Perfil";
import Chat from "./components/Chat";
import TelaBoasVindas from "./components/TelaBoasVindas";
import Login from "./components/Login";
import Cadastro from "./components/Cadastro";
import EntrarComCodigo from "./components/EntrarComCodigo";
import EscolherPlano from "./components/EscolherPlano";
import PainelLider from "./components/PainelLider";
import { GOLD, GOLD2, BLUE, TEMAS, TONS, VOZ_CORES } from "./constants/temas";
import { transporCifra, calcularCapotraste, isAcorde } from "./Transposer";
import { auth, musicas as musicasAPI, eventos as eventosAPI, salvarToken, removerToken } from "./Api";

function lerLS(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } }
function salvarLS(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} }

// ── Normaliza evento do backend: foto_look → fotoPaleta ───────────────────────
// O banco salva como foto_look, mas o frontend usa fotoPaleta
function normalizarEvento(ev) {
    return {
        ...ev,
        fotoPaleta: ev.fotoPaleta || ev.foto_look || null,
    };
}

function normalizarMusica(m) {
    return {
        ...m,
        partituraUrl:  m.partituraUrl  || m.partitura_url  || null,
        partituranome: m.partituranome || m.partitura_nome  || null,
        audioPrincipal: m.audioPrincipal || m.audio_principal || null,
    };
}


// ── Diagramas de violão — múltiplas variações por acorde ──────────────────────
// Formato: cada acorde tem um ARRAY de variações, cada uma com { cordas, posicao, barra?, label? }
const DIAGRAMAS_VIOLAO = {
    "C":  [
        { cordas:[-1,3,2,0,1,0], posicao:1, label:"Aberta" },
        { cordas:[-1,3,5,5,5,3], posicao:3, barra:{casa:3,de:1,ate:5}, label:"Barra 3ª" },
        { cordas:[8,10,10,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" },
    ],
    "D":  [
        { cordas:[-1,-1,0,2,3,2], posicao:1, label:"Aberta" },
        { cordas:[-1,5,7,7,7,5], posicao:5, barra:{casa:5,de:1,ate:5}, label:"Barra 5ª" },
        { cordas:[-1,-1,0,2,3,5], posicao:1, label:"Variação" },
    ],
    "E":  [
        { cordas:[0,2,2,1,0,0], posicao:1, label:"Aberta" },
        { cordas:[0,7,9,9,9,7], posicao:7, barra:{casa:7,de:0,ate:5}, label:"Barra 7ª" },
        { cordas:[0,2,2,4,5,4], posicao:1, label:"Variação" },
    ],
    "F":  [
        { cordas:[1,1,2,3,3,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" },
        { cordas:[-1,8,10,10,10,8], posicao:8, barra:{casa:8,de:1,ate:5}, label:"Barra 8ª" },
        { cordas:[1,1,3,3,2,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
    ],
    "G":  [
        { cordas:[3,2,0,0,0,3], posicao:1, label:"Aberta" },
        { cordas:[3,2,0,0,3,3], posicao:1, label:"Variação" },
        { cordas:[3,5,5,4,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" },
    ],
    "A":  [
        { cordas:[-1,0,2,2,2,0], posicao:1, label:"Aberta" },
        { cordas:[-1,0,2,2,2,5], posicao:1, label:"Variação" },
        { cordas:[-1,0,7,6,5,5], posicao:5, barra:{casa:5,de:0,ate:4}, label:"Barra 5ª" },
    ],
    "B":  [
        { cordas:[-1,2,4,4,4,2], posicao:2, barra:{casa:2,de:1,ate:5}, label:"Barra 2ª" },
        { cordas:[-1,2,4,4,4,4], posicao:2, label:"Variação" },
        { cordas:[-1,7,9,9,9,7], posicao:7, barra:{casa:7,de:1,ate:5}, label:"Barra 7ª" },
    ],
    "Bb": [
        { cordas:[-1,1,3,3,3,1], posicao:1, barra:{casa:1,de:1,ate:5}, label:"Barra 1ª" },
        { cordas:[-1,1,3,3,3,3], posicao:1, label:"Variação" },
        { cordas:[-1,6,8,8,8,6], posicao:6, barra:{casa:6,de:1,ate:5}, label:"Barra 6ª" },
    ],
    "C#": [
        { cordas:[-1,4,6,6,6,4], posicao:4, barra:{casa:4,de:1,ate:5}, label:"Barra 4ª" },
        { cordas:[-1,4,3,1,2,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[-1,4,6,6,6,6], posicao:4, label:"Variação 2" },
    ],
    "Db": [
        { cordas:[-1,4,6,6,6,4], posicao:4, barra:{casa:4,de:1,ate:5}, label:"Barra 4ª" },
        { cordas:[-1,4,3,1,2,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[-1,4,6,6,6,6], posicao:4, label:"Variação 2" },
    ],
    "D#": [
        { cordas:[-1,-1,1,3,4,3], posicao:1, label:"Aberta" },
        { cordas:[-1,6,8,8,8,6], posicao:6, barra:{casa:6,de:1,ate:5}, label:"Barra 6ª" },
        { cordas:[-1,-1,5,3,4,3], posicao:3, label:"Variação" },
    ],
    "Eb": [
        { cordas:[-1,-1,1,3,4,3], posicao:1, label:"Aberta" },
        { cordas:[-1,6,8,8,8,6], posicao:6, barra:{casa:6,de:1,ate:5}, label:"Barra 6ª" },
        { cordas:[-1,6,8,8,8,8], posicao:6, label:"Variação" },
    ],
    "F#": [
        { cordas:[2,2,3,4,4,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Barra 2ª" },
        { cordas:[2,4,4,3,2,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Variação" },
        { cordas:[2,2,4,4,4,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Variação 2" },
    ],
    "Gb": [
        { cordas:[2,2,3,4,4,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Barra 2ª" },
        { cordas:[2,4,4,3,2,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Variação" },
        { cordas:[2,2,4,4,4,2], posicao:2, label:"Variação 2" },
    ],
    "G#": [
        { cordas:[4,6,6,5,4,4], posicao:4, barra:{casa:4,de:0,ate:5}, label:"Barra 4ª" },
        { cordas:[4,3,1,1,1,4], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[4,6,6,5,4,4], posicao:4, label:"Variação 2" },
    ],
    "Ab": [
        { cordas:[4,6,6,5,4,4], posicao:4, barra:{casa:4,de:0,ate:5}, label:"Barra 4ª" },
        { cordas:[4,3,1,1,1,4], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[4,6,6,5,4,4], posicao:4, label:"Variação 2" },
    ],
    "Am": [
        { cordas:[-1,0,2,2,1,0], posicao:1, label:"Aberta" },
        { cordas:[-1,0,7,5,5,5], posicao:5, barra:{casa:5,de:0,ate:4}, label:"Barra 5ª" },
        { cordas:[-1,0,2,2,1,3], posicao:1, label:"Am/G" },
    ],
    "Em": [
        { cordas:[0,2,2,0,0,0], posicao:1, label:"Aberta" },
        { cordas:[0,7,9,9,8,7], posicao:7, barra:{casa:7,de:0,ate:5}, label:"Barra 7ª" },
        { cordas:[0,2,2,0,0,3], posicao:1, label:"Variação" },
    ],
    "Dm": [
        { cordas:[-1,-1,0,2,3,1], posicao:1, label:"Aberta" },
        { cordas:[-1,5,7,7,6,5], posicao:5, barra:{casa:5,de:1,ate:5}, label:"Barra 5ª" },
        { cordas:[-1,-1,0,2,3,1], posicao:1, label:"Variação" },
    ],
    "Bm": [
        { cordas:[-1,2,4,4,3,2], posicao:2, barra:{casa:2,de:1,ate:5}, label:"Barra 2ª" },
        { cordas:[-1,2,4,4,3,4], posicao:2, label:"Variação" },
        { cordas:[-1,2,0,2,3,2], posicao:1, label:"Aberta" },
    ],
    "Cm": [
        { cordas:[-1,3,5,5,4,3], posicao:3, barra:{casa:3,de:1,ate:5}, label:"Barra 3ª" },
        { cordas:[-1,3,5,5,4,3], posicao:3, label:"Variação" },
        { cordas:[8,10,10,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" },
    ],
    "Fm": [
        { cordas:[1,3,3,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" },
        { cordas:[1,1,1,3,3,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[1,3,3,2,1,4], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação 2" },
    ],
    "Gm": [
        { cordas:[3,5,5,3,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" },
        { cordas:[3,1,0,0,3,3], posicao:1, label:"Aberta" },
        { cordas:[3,5,5,3,3,6], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Variação" },
    ],
    "F#m":[
        { cordas:[2,4,4,2,2,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Barra 2ª" },
        { cordas:[2,2,2,4,4,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Variação" },
        { cordas:[2,4,4,2,2,5], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Variação 2" },
    ],
    "C#m":[
        { cordas:[-1,4,6,6,5,4], posicao:4, barra:{casa:4,de:1,ate:5}, label:"Barra 4ª" },
        { cordas:[-1,4,6,6,5,4], posicao:4, label:"Variação" },
        { cordas:[9,11,11,9,9,9], posicao:9, barra:{casa:9,de:0,ate:5}, label:"Barra 9ª" },
    ],
    "Bbm":[
        { cordas:[-1,1,3,3,2,1], posicao:1, barra:{casa:1,de:1,ate:5}, label:"Barra 1ª" },
        { cordas:[-1,1,3,3,2,1], posicao:1, label:"Variação" },
        { cordas:[6,8,8,6,6,6], posicao:6, barra:{casa:6,de:0,ate:5}, label:"Barra 6ª" },
    ],
    "Abm":[
        { cordas:[4,6,6,4,4,4], posicao:4, barra:{casa:4,de:0,ate:5}, label:"Barra 4ª" },
        { cordas:[4,4,4,6,6,4], posicao:4, label:"Variação" },
        { cordas:[4,6,6,4,4,7], posicao:4, barra:{casa:4,de:0,ate:5}, label:"Variação 2" },
    ],
    "Ebm":[
        { cordas:[-1,-1,1,3,3,2], posicao:1, label:"Aberta" },
        { cordas:[6,8,8,6,6,6], posicao:6, barra:{casa:6,de:0,ate:5}, label:"Barra 6ª" },
        { cordas:[-1,6,8,8,7,6], posicao:6, barra:{casa:6,de:1,ate:5}, label:"Variação" },
    ],
    "C7": [
        { cordas:[-1,3,2,3,1,0], posicao:1, label:"Aberta" },
        { cordas:[-1,3,5,3,5,3], posicao:3, barra:{casa:3,de:1,ate:5}, label:"Barra 3ª" },
        { cordas:[8,10,8,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" },
    ],
    "D7": [
        { cordas:[-1,-1,0,2,1,2], posicao:1, label:"Aberta" },
        { cordas:[-1,5,7,5,7,5], posicao:5, barra:{casa:5,de:1,ate:5}, label:"Barra 5ª" },
        { cordas:[-1,-1,0,2,1,0], posicao:1, label:"Variação" },
    ],
    "E7": [
        { cordas:[0,2,0,1,0,0], posicao:1, label:"Aberta" },
        { cordas:[0,2,2,1,3,0], posicao:1, label:"Variação" },
        { cordas:[0,7,7,7,7,7], posicao:7, barra:{casa:7,de:0,ate:5}, label:"Barra 7ª" },
    ],
    "F7": [
        { cordas:[1,1,1,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" },
        { cordas:[1,3,1,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[1,1,3,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação 2" },
    ],
    "G7": [
        { cordas:[3,2,0,0,0,1], posicao:1, label:"Aberta" },
        { cordas:[3,2,0,0,3,1], posicao:1, label:"Variação" },
        { cordas:[3,5,3,4,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" },
    ],
    "A7": [
        { cordas:[-1,0,2,0,2,0], posicao:1, label:"Aberta" },
        { cordas:[-1,0,2,2,2,3], posicao:1, label:"Variação" },
        { cordas:[-1,0,7,5,7,5], posicao:5, label:"Barra 5ª" },
    ],
    "B7": [
        { cordas:[-1,2,1,2,0,2], posicao:1, label:"Aberta" },
        { cordas:[-1,2,4,2,4,2], posicao:2, barra:{casa:2,de:1,ate:5}, label:"Barra 2ª" },
        { cordas:[-1,2,1,2,0,0], posicao:1, label:"Variação" },
    ],
    "Bb7":[
        { cordas:[-1,1,3,1,3,1], posicao:1, barra:{casa:1,de:1,ate:5}, label:"Barra 1ª" },
        { cordas:[-1,1,3,1,3,3], posicao:1, label:"Variação" },
        { cordas:[-1,6,8,6,8,6], posicao:6, barra:{casa:6,de:1,ate:5}, label:"Barra 6ª" },
    ],
    "Am7":[
        { cordas:[-1,0,2,0,1,0], posicao:1, label:"Aberta" },
        { cordas:[-1,0,2,2,1,3], posicao:1, label:"Variação" },
        { cordas:[-1,0,7,5,8,5], posicao:5, label:"Barra 5ª" },
    ],
    "Em7":[
        { cordas:[0,2,0,0,0,0], posicao:1, label:"Aberta" },
        { cordas:[0,2,2,0,3,0], posicao:1, label:"Variação" },
        { cordas:[0,7,7,7,8,7], posicao:7, barra:{casa:7,de:0,ate:5}, label:"Barra 7ª" },
    ],
    "Dm7":[
        { cordas:[-1,-1,0,2,1,1], posicao:1, label:"Aberta" },
        { cordas:[-1,5,7,5,6,5], posicao:5, barra:{casa:5,de:1,ate:5}, label:"Barra 5ª" },
        { cordas:[-1,-1,0,2,1,3], posicao:1, label:"Variação" },
    ],
    "Bm7":[
        { cordas:[-1,2,4,2,3,2], posicao:2, barra:{casa:2,de:1,ate:5}, label:"Barra 2ª" },
        { cordas:[-1,2,0,2,0,2], posicao:1, label:"Aberta" },
        { cordas:[-1,2,4,2,3,5], posicao:2, label:"Variação" },
    ],
    "Cm7":[
        { cordas:[-1,3,5,3,4,3], posicao:3, barra:{casa:3,de:1,ate:5}, label:"Barra 3ª" },
        { cordas:[8,10,8,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" },
        { cordas:[-1,3,5,3,4,6], posicao:3, barra:{casa:3,de:1,ate:5}, label:"Variação" },
    ],
    "Fm7":[
        { cordas:[1,1,1,1,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" },
        { cordas:[1,3,1,1,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" },
        { cordas:[1,1,3,1,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação 2" },
    ],
    "Gm7":[
        { cordas:[3,5,3,3,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" },
        { cordas:[3,1,3,0,3,3], posicao:1, label:"Aberta" },
        { cordas:[3,5,3,3,6,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Variação" },
    ],
    "F#m7":[ { cordas:[2,2,2,2,2,2], posicao:2, barra:{casa:2,de:0,ate:5}, label:"Barra 2ª" }, { cordas:[2,4,2,2,2,2], posicao:2, label:"Variação" }, { cordas:[2,2,4,2,2,2], posicao:2, label:"Variação 2" } ],
    "C#m7":[ { cordas:[-1,4,6,4,5,4], posicao:4, barra:{casa:4,de:1,ate:5}, label:"Barra 4ª" }, { cordas:[9,11,9,9,9,9], posicao:9, barra:{casa:9,de:0,ate:5}, label:"Barra 9ª" }, { cordas:[-1,4,6,4,5,7], posicao:4, label:"Variação" } ],
    "C7M":[
        { cordas:[-1,3,2,0,0,0], posicao:1, label:"Aberta" },
        { cordas:[-1,3,2,4,1,0], posicao:1, label:"Variação" },
        { cordas:[8,10,9,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" },
    ],
    "CM7": [ { cordas:[-1,3,2,0,0,0], posicao:1, label:"Aberta" }, { cordas:[-1,3,2,4,1,0], posicao:1, label:"Variação" }, { cordas:[8,10,9,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" } ],
    "Cmaj7":[ { cordas:[-1,3,2,0,0,0], posicao:1, label:"Aberta" }, { cordas:[-1,3,2,4,1,0], posicao:1, label:"Variação" }, { cordas:[8,10,9,9,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" } ],
    "D7M":[
        { cordas:[-1,-1,0,2,2,2], posicao:1, label:"Aberta" },
        { cordas:[-1,5,7,6,7,5], posicao:5, label:"Barra 5ª" },
        { cordas:[-1,-1,0,2,2,0], posicao:1, label:"Variação" },
    ],
    "DM7": [ { cordas:[-1,-1,0,2,2,2], posicao:1, label:"Aberta" }, { cordas:[-1,5,7,6,7,5], posicao:5, label:"Barra 5ª" }, { cordas:[-1,-1,0,2,2,0], posicao:1, label:"Variação" } ],
    "E7M":[
        { cordas:[0,2,1,1,0,0], posicao:1, label:"Aberta" },
        { cordas:[0,2,1,1,4,0], posicao:1, label:"Variação" },
        { cordas:[0,7,9,8,9,7], posicao:7, label:"Barra 7ª" },
    ],
    "EM7": [ { cordas:[0,2,1,1,0,0], posicao:1, label:"Aberta" }, { cordas:[0,2,1,1,4,0], posicao:1, label:"Variação" }, { cordas:[0,7,9,8,9,7], posicao:7, label:"Barra 7ª" } ],
    "F7M":[
        { cordas:[-1,0,3,2,1,0], posicao:1, label:"Aberta" },
        { cordas:[1,0,2,2,1,0], posicao:1, label:"Variação" },
        { cordas:[1,3,2,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" },
    ],
    "FM7": [ { cordas:[-1,0,3,2,1,0], posicao:1, label:"Aberta" }, { cordas:[1,0,2,2,1,0], posicao:1, label:"Variação" }, { cordas:[1,3,2,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" } ],
    "Fmaj7":[ { cordas:[-1,0,3,2,1,0], posicao:1, label:"Aberta" }, { cordas:[1,0,2,2,1,0], posicao:1, label:"Variação" }, { cordas:[1,3,2,2,1,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" } ],
    "G7M":[
        { cordas:[3,2,0,0,0,2], posicao:1, label:"Aberta" },
        { cordas:[3,2,4,4,4,2], posicao:2, label:"Variação" },
        { cordas:[3,5,4,4,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" },
    ],
    "GM7": [ { cordas:[3,2,0,0,0,2], posicao:1, label:"Aberta" }, { cordas:[3,2,4,4,4,2], posicao:2, label:"Variação" }, { cordas:[3,5,4,4,3,3], posicao:3, label:"Barra 3ª" } ],
    "A7M":[
        { cordas:[-1,0,2,1,2,0], posicao:1, label:"Aberta" },
        { cordas:[-1,0,2,1,2,2], posicao:1, label:"Variação" },
        { cordas:[-1,0,7,6,9,5], posicao:5, label:"Barra 5ª" },
    ],
    "AM7": [ { cordas:[-1,0,2,1,2,0], posicao:1, label:"Aberta" }, { cordas:[-1,0,2,1,2,2], posicao:1, label:"Variação" }, { cordas:[-1,0,7,6,9,5], posicao:5, label:"Barra 5ª" } ],
    "B7M":[
        { cordas:[-1,2,4,3,4,2], posicao:2, barra:{casa:2,de:1,ate:5}, label:"Barra 2ª" },
        { cordas:[-1,2,1,3,0,2], posicao:1, label:"Aberta" },
        { cordas:[-1,2,4,3,4,5], posicao:2, label:"Variação" },
    ],
    "BM7": [ { cordas:[-1,2,4,3,4,2], posicao:2, barra:{casa:2,de:1,ate:5}, label:"Barra 2ª" }, { cordas:[-1,2,1,3,0,2], posicao:1, label:"Aberta" }, { cordas:[-1,2,4,3,4,5], posicao:2, label:"Variação" } ],
    "Am7M":[ { cordas:[-1,0,2,1,1,0], posicao:1, label:"Aberta" }, { cordas:[-1,0,2,1,1,3], posicao:1, label:"Variação" }, { cordas:[-1,0,2,1,1,0], posicao:1, label:"Variação 2" } ],
    "C9":  [ { cordas:[-1,3,2,3,3,3], posicao:1, label:"Aberta" }, { cordas:[-1,3,2,3,3,0], posicao:1, label:"Variação" }, { cordas:[8,10,8,9,8,10], posicao:8, label:"Barra 8ª" } ],
    "D9":  [ { cordas:[-1,-1,0,2,1,0], posicao:1, label:"Aberta" }, { cordas:[-1,5,7,5,7,7], posicao:5, label:"Barra 5ª" }, { cordas:[-1,-1,0,4,1,0], posicao:1, label:"Variação" } ],
    "G9":  [ { cordas:[3,2,0,2,0,1], posicao:1, label:"Aberta" }, { cordas:[3,5,3,4,3,5], posicao:3, label:"Barra 3ª" }, { cordas:[3,2,3,2,3,3], posicao:2, label:"Variação" } ],
    "A9":  [ { cordas:[-1,0,2,4,2,3], posicao:1, label:"Aberta" }, { cordas:[-1,0,7,6,7,7], posicao:5, label:"Barra 5ª" }, { cordas:[-1,0,2,0,2,3], posicao:1, label:"Variação" } ],
    "F9":  [ { cordas:[1,3,1,2,1,3], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Barra 1ª" }, { cordas:[1,3,1,2,4,1], posicao:1, barra:{casa:1,de:0,ate:5}, label:"Variação" }, { cordas:[8,10,8,9,8,10], posicao:8, label:"Barra 8ª" } ],
    "E9":  [ { cordas:[0,2,0,1,3,2], posicao:1, label:"Aberta" }, { cordas:[0,2,0,1,0,2], posicao:1, label:"Variação" }, { cordas:[0,7,7,7,7,9], posicao:7, label:"Barra 7ª" } ],
    "Csus4":[ { cordas:[-1,3,3,0,1,1], posicao:1, label:"Aberta" }, { cordas:[-1,3,3,0,1,3], posicao:1, label:"Variação" }, { cordas:[8,10,10,10,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" } ],
    "Dsus4":[ { cordas:[-1,-1,0,2,3,3], posicao:1, label:"Aberta" }, { cordas:[-1,5,7,7,8,5], posicao:5, label:"Barra 5ª" }, { cordas:[-1,-1,0,0,3,3], posicao:1, label:"Variação" } ],
    "Gsus4":[ { cordas:[3,3,0,0,1,3], posicao:1, label:"Aberta" }, { cordas:[3,5,5,5,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" }, { cordas:[3,3,0,5,1,3], posicao:1, label:"Variação" } ],
    "Asus4":[ { cordas:[-1,0,2,2,3,0], posicao:1, label:"Aberta" }, { cordas:[-1,0,7,7,7,5], posicao:5, label:"Barra 5ª" }, { cordas:[-1,0,2,4,3,0], posicao:1, label:"Variação" } ],
    "Esus4":[ { cordas:[0,2,2,2,0,0], posicao:1, label:"Aberta" }, { cordas:[0,7,9,9,10,7], posicao:7, label:"Barra 7ª" }, { cordas:[0,2,4,2,0,0], posicao:1, label:"Variação" } ],
    "C4":  [ { cordas:[-1,3,3,0,1,1], posicao:1, label:"Aberta" }, { cordas:[8,10,10,10,8,8], posicao:8, barra:{casa:8,de:0,ate:5}, label:"Barra 8ª" }, { cordas:[-1,3,3,0,1,3], posicao:1, label:"Variação" } ],
    "D4":  [ { cordas:[-1,-1,0,2,3,3], posicao:1, label:"Aberta" }, { cordas:[-1,5,7,7,8,5], posicao:5, label:"Barra 5ª" }, { cordas:[-1,-1,0,4,3,3], posicao:1, label:"Variação" } ],
    "G4":  [ { cordas:[3,3,0,0,1,3], posicao:1, label:"Aberta" }, { cordas:[3,5,5,5,3,3], posicao:3, barra:{casa:3,de:0,ate:5}, label:"Barra 3ª" }, { cordas:[3,3,0,0,1,1], posicao:1, label:"Variação" } ],
    "A4":  [ { cordas:[-1,0,2,2,3,0], posicao:1, label:"Aberta" }, { cordas:[-1,0,7,7,7,5], posicao:5, label:"Barra 5ª" }, { cordas:[-1,0,2,4,3,5], posicao:1, label:"Variação" } ],
    "E4":  [ { cordas:[0,2,2,2,0,0], posicao:1, label:"Aberta" }, { cordas:[0,7,9,9,10,7], posicao:7, label:"Barra 7ª" }, { cordas:[0,2,4,2,0,5], posicao:1, label:"Variação" } ],
    "Bdim":[ { cordas:[-1,2,3,4,3,-1], posicao:1, label:"Aberta" }, { cordas:[-1,2,0,1,0,-1], posicao:1, label:"Variação" }, { cordas:[-1,2,3,1,3,-1], posicao:1, label:"Variação 2" } ],
    "Cdim":[ { cordas:[-1,3,4,5,4,-1], posicao:1, label:"Aberta" }, { cordas:[-1,3,1,2,1,-1], posicao:1, label:"Variação" }, { cordas:[-1,3,4,2,4,-1], posicao:1, label:"Variação 2" } ],
    "Ddim":[ { cordas:[-1,-1,0,1,0,1], posicao:1, label:"Aberta" }, { cordas:[-1,-1,0,4,3,4], posicao:1, label:"Variação" }, { cordas:[-1,5,6,7,6,-1], posicao:5, label:"Barra 5ª" } ],
    "Edim":[ { cordas:[0,1,2,3,2,-1], posicao:1, label:"Aberta" }, { cordas:[0,1,2,0,2,-1], posicao:1, label:"Variação" }, { cordas:[3,4,5,6,5,-1], posicao:3, label:"Barra 3ª" } ],
    "F7M/A":[ { cordas:[-1,0,3,2,1,0], posicao:1, label:"F7M/A" }, { cordas:[1,0,2,2,1,0], posicao:1, label:"Variação" }, { cordas:[-1,0,3,2,1,3], posicao:1, label:"Variação 2" } ],
    "G/B": [ { cordas:[-1,2,0,0,0,3], posicao:1, label:"G/B" }, { cordas:[-1,2,0,0,3,3], posicao:1, label:"Variação" }, { cordas:[3,2,0,0,0,3], posicao:1, label:"G" } ],
    "D/F#":[ { cordas:[2,0,0,2,3,2], posicao:1, label:"D/F#" }, { cordas:[2,0,0,2,3,0], posicao:1, label:"Variação" }, { cordas:[2,4,4,2,3,2], posicao:2, label:"Variação 2" } ],
    "C/E": [ { cordas:[0,3,2,0,1,0], posicao:1, label:"C/E" }, { cordas:[0,3,2,0,1,3], posicao:1, label:"Variação" }, { cordas:[-1,3,2,0,1,0], posicao:1, label:"C" } ],
    "Am/E":[ { cordas:[0,0,2,2,1,0], posicao:1, label:"Am/E" }, { cordas:[0,0,2,2,1,3], posicao:1, label:"Variação" }, { cordas:[-1,0,2,2,1,0], posicao:1, label:"Am" } ],
};

// Busca variações do acorde — retorna array com todas as variações
function buscarVariacoesViolao(acorde) {
    if (DIAGRAMAS_VIOLAO[acorde]) return DIAGRAMAS_VIOLAO[acorde];
    const aliases = { "7M": ["M7","maj7"], "M7": ["7M","maj7"], "maj7": ["7M","M7"], "m7M": ["mM7"], "mM7": ["m7M"] };
    for (const [suf, alts] of Object.entries(aliases)) {
        if (acorde.endsWith(suf)) {
            const base = acorde.slice(0, -suf.length);
            for (const alt of alts) { if (DIAGRAMAS_VIOLAO[base + alt]) return DIAGRAMAS_VIOLAO[base + alt]; }
        }
    }
    const semBaixo = acorde.split("/")[0];
    if (semBaixo !== acorde && DIAGRAMAS_VIOLAO[semBaixo]) return DIAGRAMAS_VIOLAO[semBaixo];
    const notaBase = acorde.match(/^[A-G][#b]?/)?.[0];
    const sufixo = notaBase ? acorde.slice(notaBase.length).split("/")[0] : "";
    const ehMenor = sufixo.startsWith("m") || sufixo.startsWith("min");
    if (ehMenor && DIAGRAMAS_VIOLAO[notaBase + "m"]) return DIAGRAMAS_VIOLAO[notaBase + "m"];
    if (!ehMenor && notaBase && DIAGRAMAS_VIOLAO[notaBase]) return DIAGRAMAS_VIOLAO[notaBase];
    return null;
}

// Mantém compatibilidade com código que usa buscarDiagramaViolao (retorna primeira variação)
function buscarDiagramaViolao(acorde) {
    const vars = buscarVariacoesViolao(acorde);
    return vars ? vars[0] : null;
}

const DIAGRAMAS_PIANO = {
    "C":[0,4,7],"D":[2,6,9],"E":[4,8,11],"F":[5,9,0],"G":[7,11,2],
    "A":[9,1,4],"B":[11,3,6],"Bb":[10,2,5],"C#":[1,5,8],"Db":[1,5,8],
    "D#":[3,7,10],"Eb":[3,7,10],"F#":[6,10,1],"Gb":[6,10,1],
    "G#":[8,0,3],"Ab":[8,0,3],
    "Am":[9,0,4],"Em":[4,7,11],"Dm":[2,5,9],"Bm":[11,2,6],
    "Cm":[0,3,7],"Fm":[5,8,0],"Gm":[7,10,2],"F#m":[6,9,1],
    "Am7":[9,0,4,7],"Em7":[4,7,11,2],"Dm7":[2,5,9,0],
    "C7":[0,4,7,10],"G7":[7,11,2,5],"D7":[2,6,9,0],"E7":[4,8,11,2],
    "A7":[9,1,4,7],"B7":[11,3,6,9],"F7":[5,9,0,3],
    "C7M":[0,4,7,11],"CM7":[0,4,7,11],"Cmaj7":[0,4,7,11],
    "F7M":[5,9,0,4],"FM7":[5,9,0,4],"Fmaj7":[5,9,0,4],
    "G7M":[7,11,2,6],"GM7":[7,11,2,6],
    "D7M":[2,6,9,1],"DM7":[2,6,9,1],
    "A7M":[9,1,4,8],"AM7":[9,1,4,8],
    "E7M":[4,8,11,3],"EM7":[4,8,11,3],
    "B7M":[11,3,6,10],"BM7":[11,3,6,10],
    "F9":[5,9,0,3,7],"C9":[0,4,7,10,2],"G9":[7,11,2,5,9],
    "F#m7":[6,9,1,5],"C#m7":[1,4,8,0],
    "Bm7":[11,2,6,9],"Cm7":[0,3,7,10],
};

function buscarDiagramaPiano(acorde) {
    if (DIAGRAMAS_PIANO[acorde]) return DIAGRAMAS_PIANO[acorde];
    const semBaixo = acorde.split("/")[0];
    if (DIAGRAMAS_PIANO[semBaixo]) return DIAGRAMAS_PIANO[semBaixo];
    const notaBase = acorde.match(/^[A-G][#b]?/)?.[0];
    const sufixo = notaBase ? acorde.slice(notaBase.length).split("/")[0] : "";
    const ehMenor = sufixo.startsWith("m") || sufixo.startsWith("min");
    if (ehMenor && DIAGRAMAS_PIANO[notaBase + "m"]) return DIAGRAMAS_PIANO[notaBase + "m"];
    if (!ehMenor && notaBase && DIAGRAMAS_PIANO[notaBase]) return DIAGRAMAS_PIANO[notaBase];
    return null;
}

function DiagramaPiano({ acorde, tema }) {
    const T = tema;
    const notas = buscarDiagramaPiano(acorde);
    if (!notas) return <div style={{fontSize:11,color:T.textoSecundario,padding:8}}>Diagrama de {acorde} indisponível</div>;
    const BRANCAS = [0,2,4,5,7,9,11,12,14,16,17,19,21,23];
    const PRETAS_POS = [0.65,1.65,3.65,4.65,5.65,7.65,8.65,10.65,11.65,12.65];
    const PRETAS_MIDI = [1,3,6,8,10,13,15,18,20,22];
    const W = 112, H = 52, tw = W / BRANCAS.length;
    return (
        <div style={{ textAlign: "center", marginRight: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: GOLD, marginBottom: 4 }}>{acorde}</div>
            <svg width={W} height={H} style={{ display: "block", margin: "0 auto", borderRadius: 4, overflow: "hidden" }}>
                {BRANCAS.map((midi, i) => <rect key={i} x={i * tw} y={0} width={tw - 1} height={H} fill={notas.includes(midi % 12) ? GOLD : "white"} stroke={T.borda} strokeWidth={0.5} rx={i === 0 ? 3 : i === BRANCAS.length-1 ? 3 : 0} />)}
                {PRETAS_POS.map((pos, i) => <rect key={i} x={pos * tw} y={0} width={tw * 0.65} height={H * 0.62} fill={notas.includes(PRETAS_MIDI[i] % 12) ? GOLD : "#1a1a2e"} rx={3} />)}
            </svg>
        </div>
    );
}

function SeletorCapo({ sugestoes, capoAtivo, onSelectCapo, tema }) {
    const T = tema;
    if (!sugestoes?.length) return null;
    const NUM_CASAS = 8, CELL_H = 26, CELL_W = 34, PAD_L = 10;
    const H = CELL_H * NUM_CASAS + 10, W = PAD_L + CELL_W * 2 + 10;
    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: T.textoSecundario, marginBottom: 8, fontWeight: 600 }}>🎸 Toque uma casa para aplicar o capotraste:</div>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <svg width={W} height={H} style={{ cursor: "pointer" }}>
                    <rect x={PAD_L} y={0} width={CELL_W} height={5} rx={2} fill="#ccc" />
                    {[0,0.5,1].map((frac, i) => <line key={i} x1={PAD_L + frac * CELL_W} y1={5} x2={PAD_L + frac * CELL_W} y2={H} stroke={T.borda} strokeWidth={1.5} />)}
                    {Array.from({ length: NUM_CASAS }, (_, i) => {
                        const ativa = capoAtivo === i + 1;
                        return (
                            <g key={i} onClick={() => onSelectCapo(ativa ? 0 : i + 1)} style={{ cursor: "pointer" }}>
                                <rect x={0} y={i * CELL_H + 5} width={W} height={CELL_H} fill={ativa ? `${GOLD}22` : "transparent"} />
                                <line x1={PAD_L - 6} y1={(i + 1) * CELL_H + 5} x2={PAD_L + CELL_W + 6} y2={(i + 1) * CELL_H + 5} stroke={T.borda} strokeWidth={1} />
                                {ativa && <rect x={PAD_L - 4} y={i * CELL_H + 8} width={CELL_W + 8} height={CELL_H - 8} rx={4} fill={GOLD} opacity={0.9} />}
                                <text x={PAD_L + CELL_W + 10} y={i * CELL_H + CELL_H / 2 + 9} fontSize={10} fill={ativa ? GOLD : T.textoSecundario} fontWeight={ativa ? "800" : "400"}>{i + 1}ª</text>
                            </g>
                        );
                    })}
                </svg>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: T.textoSecundario, marginBottom: 6 }}>Resultado:</div>
                    {sugestoes.filter(s => s.capo > 0 && s.capo <= NUM_CASAS).map((s, i) => (
                        <button key={i} onClick={() => onSelectCapo(capoAtivo === s.capo ? 0 : s.capo)}
                                style={{ display: "block", width: "100%", textAlign: "left", background: capoAtivo === s.capo ? `${GOLD}20` : T.bgCardSecundario, border: `1px solid ${capoAtivo === s.capo ? GOLD : T.borda}`, borderRadius: 8, padding: "5px 10px", marginBottom: 5, cursor: "pointer", fontSize: 12, color: capoAtivo === s.capo ? GOLD : T.texto, fontWeight: capoAtivo === s.capo ? 700 : 400 }}>
                            Capo {s.capo} → toca em <strong>{s.tomTocar}</strong>
                        </button>
                    ))}
                </div>
            </div>
            {capoAtivo > 0 && (
                <div style={{ marginTop: 8, padding: "6px 12px", background: `${GOLD}15`, borderRadius: 10, border: `1px solid ${GOLD}33`, fontSize: 12, color: T.texto }}>
                    🎸 Com capo na <strong style={{color:GOLD}}>{capoAtivo}ª casa</strong>, toca em <strong style={{color:GOLD}}>{sugestoes.find(s=>s.capo===capoAtivo)?.tomTocar}</strong>
                </div>
            )}
        </div>
    );
}


function DiagramaViolaoSVG({ d, tema: T, tamanho = "normal" }) {
    if (!d) return null;
    const { cordas, posicao = 1, barra } = d;
    const NUM_CORDAS = 6, NUM_CASAS = 4;
    const CELL_W = tamanho === "mini" ? 16 : 22;
    const CELL_H = tamanho === "mini" ? 15 : 20;
    const PAD_L  = tamanho === "mini" ? 14 : 20;
    const PAD_T  = tamanho === "mini" ? 22 : 28;
    const W = PAD_L + CELL_W * (NUM_CORDAS - 1) + (tamanho === "mini" ? 14 : 20);
    const H = PAD_T + CELL_H * NUM_CASAS + (tamanho === "mini" ? 8 : 12);
    const cx = (i) => PAD_L + i * CELL_W;
    const cy = (i) => PAD_T + i * CELL_H;
    return (
        <svg width={W} height={H} style={{ display: "block", margin: "0 auto" }}>
            {posicao > 1 && <text x={PAD_L - 4} y={PAD_T + CELL_H * 0.7} fontSize={tamanho === "mini" ? 7 : 9} fill={T.textoSecundario} textAnchor="end">{posicao}ª</text>}
            {posicao === 1 && <rect x={PAD_L} y={PAD_T - 3} width={CELL_W * (NUM_CORDAS - 1)} height={tamanho === "mini" ? 4 : 5} rx={2} fill={T.texto} />}
            {Array.from({ length: NUM_CASAS + 1 }, (_, i) => <line key={i} x1={PAD_L} y1={cy(i)} x2={PAD_L + CELL_W * (NUM_CORDAS - 1)} y2={cy(i)} stroke={T.borda} strokeWidth={1} />)}
            {Array.from({ length: NUM_CORDAS }, (_, i) => <line key={i} x1={cx(i)} y1={cy(0)} x2={cx(i)} y2={cy(NUM_CASAS)} stroke={T.borda} strokeWidth={1} />)}
            {barra && <rect x={cx(barra.de) + 2} y={cy(barra.casa - posicao) + 3} width={cx(barra.ate) - cx(barra.de) - 4} height={CELL_H - 6} rx={tamanho === "mini" ? 3 : 5} fill={GOLD} />}
            {cordas.map((casa, ci) => {
                if (casa <= 0) return null;
                const casaRel = casa - posicao;
                if (casaRel < 0 || casaRel >= NUM_CASAS) return null;
                if (barra && ci >= barra.de && ci <= barra.ate && casa === barra.casa) return null;
                return <circle key={ci} cx={cx(ci)} cy={cy(casaRel) + CELL_H / 2} r={tamanho === "mini" ? 5 : 7} fill={GOLD} />;
            })}
            {cordas.map((casa, ci) => {
                if (casa === -1) return <text key={ci} x={cx(ci)} y={PAD_T - 6} textAnchor="middle" fontSize={tamanho === "mini" ? 9 : 11} fontWeight="700" fill={T.textoSecundario}>×</text>;
                if (casa === 0) return <text key={ci} x={cx(ci)} y={PAD_T - 6} textAnchor="middle" fontSize={tamanho === "mini" ? 10 : 12} fill={GOLD}>○</text>;
                return null;
            })}
        </svg>
    );
}

// Popup estilo CifraClub com múltiplas variações
function PopupDiagrama({ acorde, instrumento, tema: T, onFechar }) {
    const [varIdx, setVarIdx] = useState(0);
    const [mostrarVariacoes, setMostrarVariacoes] = useState(false);
    const variacoes = instrumento === "violao" ? (buscarVariacoesViolao(acorde) || []) : null;
    const notasPiano = buscarDiagramaPiano(acorde);

    useEffect(() => { setVarIdx(0); setMostrarVariacoes(false); }, [acorde]);

    if (!acorde) return null;

    return (
        <div style={{
            position: "sticky", top: 0, zIndex: 10,
            background: T.bgCard,
            border: "1px solid " + GOLD + "44",
            borderRadius: 16,
            marginBottom: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            overflow: "hidden",
        }}>
            {/* Header — nome do acorde + fechar */}
            <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px",
                borderBottom: "1px solid " + T.borda,
                background: T.bgCardSecundario,
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 900, color: GOLD }}>{acorde}</span>
                    {instrumento === "violao" && variacoes && variacoes[varIdx]?.label && (
                        <span style={{ fontSize: 10, color: T.textoSecundario, background: T.bgCard, borderRadius: 8, padding: "2px 8px", border: "1px solid " + T.borda }}>
                            {variacoes[varIdx].label}
                        </span>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {instrumento === "violao" && variacoes && variacoes.length > 1 && (
                        <button
                            onClick={() => setMostrarVariacoes(p => !p)}
                            style={{
                                fontSize: 11, fontWeight: 700,
                                background: mostrarVariacoes ? GOLD : T.bgCard,
                                color: mostrarVariacoes ? "#0f1729" : GOLD,
                                border: "1px solid " + GOLD + "55",
                                borderRadius: 20, padding: "4px 10px",
                                cursor: "pointer",
                            }}
                        >
                            variar acorde
                        </button>
                    )}
                    <button onClick={onFechar} style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: T.bgCard, border: "1px solid " + T.borda,
                        color: T.textoSecundario, cursor: "pointer",
                        fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>✕</button>
                </div>
            </div>

            {/* Diagrama principal */}
            <div style={{ padding: "16px 20px" }}>
                {instrumento === "violao" && variacoes && variacoes.length > 0 ? (
                    <div>
                        {/* Variações — só aparece ao clicar "variar acorde" */}
                        {mostrarVariacoes && (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
                                    {variacoes.map((v, i) => (
                                        <button key={i} onClick={() => { setVarIdx(i); setMostrarVariacoes(false); }}
                                                style={{
                                                    flexShrink: 0, padding: "10px 10px 6px",
                                                    borderRadius: 12,
                                                    border: "2px solid " + (varIdx === i ? GOLD : T.borda),
                                                    background: varIdx === i ? GOLD + "15" : T.bgCardSecundario,
                                                    cursor: "pointer", transition: "all 0.15s",
                                                }}
                                        >
                                            <DiagramaViolaoSVG d={v} tema={T} tamanho="mini" />
                                            <div style={{ fontSize: 10, color: varIdx === i ? GOLD : T.textoSecundario, marginTop: 4, fontWeight: 600, textAlign: "center" }}>
                                                {v.label || (i + 1)}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <div style={{ height: 1, background: T.borda, marginBottom: 14 }} />
                            </div>
                        )}
                        {/* Diagrama selecionado */}
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <DiagramaViolaoSVG d={variacoes[varIdx]} tema={T} tamanho="normal" />
                        </div>
                        {/* Dots de navegação */}
                        {variacoes.length > 1 && !mostrarVariacoes && (
                            <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 12 }}>
                                {variacoes.map((_, i) => (
                                    <div key={i} onClick={() => setVarIdx(i)} style={{
                                        width: i === varIdx ? 18 : 6, height: 6, borderRadius: 3,
                                        background: i === varIdx ? GOLD : T.borda,
                                        cursor: "pointer", transition: "all 0.2s",
                                    }} />
                                ))}
                            </div>
                        )}
                    </div>
                ) : instrumento === "piano" && notasPiano ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <DiagramaPiano acorde={acorde} tema={T} />
                    </div>
                ) : (
                    <div style={{ fontSize: 12, color: T.textoSecundario, textAlign: "center", padding: 8 }}>
                        Diagrama indisponível para {acorde}
                    </div>
                )}
            </div>
        </div>
    );
}

function DiagramaViolao({ acorde, tema }) {
    const T = tema;
    const d = buscarDiagramaViolao(acorde);
    if (!d) return <div style={{fontSize:11,color:T.textoSecundario,padding:8}}>Diagrama de {acorde} indisponível</div>;
    return (
        <div style={{ textAlign: "center", marginRight: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: GOLD, marginBottom: 4 }}>{acorde}</div>
            <DiagramaViolaoSVG d={d} tema={T} tamanho="normal" />
        </div>
    );
}

function parsePares(linhas) {
    // Pré-processa: separa "[SECAO] Acorde1 Acorde2" em duas linhas
    const linhasExpandidas = [];
    for (const l of linhas) {
        const m = l.match(/^(\[[^\]]+\])\s+(.+)$/);
        if (m) {
            linhasExpandidas.push(m[1]);        // "[INTRO]"
            linhasExpandidas.push(m[2]);        // "Am7 F7M/A C C4 C"
        } else {
            linhasExpandidas.push(l);
        }
    }

    const result = [];
    let i = 0;
    while (i < linhasExpandidas.length) {
        const l = linhasExpandidas[i];
        if (!l.trim()) { result.push({ tipo: "vazia" }); i++; continue; }
        if (l.startsWith("[")) { result.push({ tipo: "secao", texto: l }); i++; continue; }
        if (isAcorde(l.trim().split(/\s+/)[0]) && ehLinhaDeAcordes(l)) {
            const proxima = linhasExpandidas[i + 1];
            const proximaELetra = proxima !== undefined && !proxima.startsWith("[") && proxima.trim() !== "" && !ehLinhaDeAcordes(proxima);
            if (proximaELetra) { result.push({ tipo: "par", acordes: l, letra: proxima }); i += 2; }
            else { result.push({ tipo: "soAcordes", acordes: l }); i++; }
        } else { result.push({ tipo: "soLetra", letra: l }); i++; }
    }
    return result;
}

function ehLinhaDeAcordes(linha) {
    if (!linha || !linha.trim()) return false;
    const tokens = linha.trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return false;
    const qtdAcordes = tokens.filter(t => isAcorde(t)).length;
    return qtdAcordes > 0 && qtdAcordes / tokens.length >= 0.5;
}

function LinhaComAcorde({ acordesStr, letraStr, acordeSel, onClickAcorde, T }) {
    const regex = /([A-G][#b]?(7M|M7|maj7|maj|min|dim|aug|sus2|sus4|sus|add|m(?=[^a]|$))?\d*(\/[A-G][#b]?)?)/g;
    return (
        <div style={{ position: "relative", marginBottom: 6, marginTop: 4 }}>
            <div style={{ fontFamily: "monospace", fontSize: 13, whiteSpace: "pre", lineHeight: 1, marginBottom: 2, minHeight: 18 }}>
                {acordesStr.split(regex).map((part, pi) => {
                    if (isAcorde(part?.trim())) {
                        const ativo = acordeSel === part.trim();
                        return <button key={pi} onClick={() => onClickAcorde(part.trim())} style={{ color: ativo ? "#fff" : GOLD, background: ativo ? GOLD : "none", border: "none", borderRadius: 4, cursor: "pointer", fontFamily: "monospace", fontSize: 13, fontWeight: 700, padding: ativo ? "0 4px" : "0", lineHeight: "inherit", verticalAlign: "baseline" }}>{part}</button>;
                    }
                    return <span key={pi} style={{ color: "transparent", userSelect: "none" }}>{part}</span>;
                })}
            </div>
            {letraStr && <div style={{ fontFamily: "monospace", fontSize: 14, color: T.texto, whiteSpace: "pre", lineHeight: 1.5 }}>{letraStr}</div>}
        </div>
    );
}

function CifraFormatada({ cifra, instrumento, tema }) {
    const T = tema;
    const [acordeSel, setAcordeSel] = useState(null);
    if (!cifra) return null;
    const pares = parsePares(cifra.split("\n"));
    const handleAcorde = (acorde) => setAcordeSel(prev => prev === acorde ? null : acorde);
    return (
        <div style={{ fontFamily: "monospace", fontSize: 14 }}>
            {acordeSel && (
                <PopupDiagrama
                    acorde={acordeSel}
                    instrumento={instrumento}
                    tema={T}
                    onFechar={() => setAcordeSel(null)}
                />
            )}
            {pares.map((item, i) => {
                if (item.tipo === "vazia") return <div key={i} style={{ height: 10 }} />;
                if (item.tipo === "secao") return <div key={i} style={{ color: GOLD, fontWeight: 700, fontSize: 12, marginTop: 18, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{item.texto}</div>;
                if (item.tipo === "par" || item.tipo === "soAcordes") return <LinhaComAcorde key={i} acordesStr={item.acordes} letraStr={item.tipo === "par" ? item.letra : ""} acordeSel={acordeSel} onClickAcorde={handleAcorde} T={T} />;
                return <div key={i} style={{ fontFamily: "monospace", fontSize: 14, color: T.texto, whiteSpace: "pre", lineHeight: 1.6, marginBottom: 2 }}>{item.letra || "\u00a0"}</div>;
            })}
        </div>
    );
}

function Musicas({ musicas, adicionarMusica, adminMode, abrirMusica, tema }) {
    const T = TEMAS[tema];
    const [showForm, setShowForm] = useState(false);
    const [busca, setBusca] = useState("");
    const [nova, setNova] = useState({ titulo: "", autor: "", versao: "", tom: "C", cifra: "" });
    const [loading, setLoading] = useState(false);

    const salvar = async () => {
        if (!nova.titulo) return;
        setLoading(true);
        try { await adicionarMusica(nova); setNova({ titulo: "", autor: "", versao: "", tom: "C", cifra: "" }); setShowForm(false); }
        finally { setLoading(false); }
    };

    const musicasFiltradas = musicas.filter(m =>
        m.titulo.toLowerCase().includes(busca.toLowerCase()) ||
        (m.autor || "").toLowerCase().includes(busca.toLowerCase())
    );

    return (
        <div style={{ padding: 16, paddingBottom: 80 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.texto }}>🎵 Músicas</div>
                {adminMode && <button onClick={() => setShowForm(!showForm)} style={{ background: GOLD, border: "none", borderRadius: 20, padding: "6px 16px", color: BLUE, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Adicionar</button>}
            </div>
            <input placeholder="🔍  Buscar música ou artista..." value={busca} onChange={e => setBusca(e.target.value)}
                   style={{ width: "100%", padding: "13px 16px", borderRadius: 14, border: `1.5px solid ${T.borda}`, background: T.input, color: T.texto, fontSize: 14, boxSizing: "border-box", marginBottom: 16, outline: "none" }} />
            {showForm && (
                <div style={{ background: T.bgCard, borderRadius: 16, padding: 16, marginBottom: 16, border: `1px solid ${T.borda}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: T.texto }}>Nova música</div>
                    {[["Título *", "titulo"], ["Autor / Cantor", "autor"], ["Versão / Arranjo", "versao"], ["Tom", "tom"]].map(([label, key]) => (
                        <div key={key} style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, color: T.textoSecundario, marginBottom: 3 }}>{label}</div>
                            <input value={nova[key]} onChange={e => setNova(p => ({ ...p, [key]: e.target.value }))}
                                   style={{ width: "100%", padding: "8px 12px", borderRadius: 10, border: `1px solid ${T.borda}`, background: T.input, color: T.texto, fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                    ))}
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, color: T.textoSecundario, marginBottom: 3 }}>Cifra</div>
                        <textarea value={nova.cifra} onChange={e => setNova(p => ({ ...p, cifra: e.target.value }))} rows={6}
                                  style={{ width: "100%", padding: "8px 12px", borderRadius: 10, border: `1px solid ${T.borda}`, background: T.input, color: T.texto, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box", resize: "vertical" }} />
                    </div>
                    <button onClick={salvar} disabled={loading} style={{ width: "100%", padding: 12, borderRadius: 10, background: GOLD, border: "none", color: BLUE, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
                        {loading ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            )}
            {musicasFiltradas.length === 0 && <div style={{ textAlign: "center", color: T.textoSecundario, marginTop: 30, fontSize: 14 }}>Nenhuma música encontrada</div>}
            {musicasFiltradas.map(m => (
                <button key={m.id} onClick={() => abrirMusica(m)}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, background: T.bgCard, borderRadius: 16, padding: "16px 18px", marginBottom: 12, border: `1px solid ${T.borda}`, cursor: "pointer", textAlign: "left" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${GOLD}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🎵</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: T.texto }}>{m.titulo}</div>
                        <div style={{ fontSize: 12, color: T.textoSecundario }}>
                            {m.autor && <span>{m.autor}</span>}
                            {m.autor && m.versao && <span> · </span>}
                            {m.versao && <span style={{ color: GOLD }}>{m.versao}</span>}
                            {!m.autor && !m.versao && <span>Tom: {m.tom}</span>}
                        </div>
                    </div>
                    <span style={{ color: T.textoSecundario }}>›</span>
                </button>
            ))}
        </div>
    );
}

function LeitorPDF({ url, musicaId }) {
    const [paginas, setPaginas] = useState([]);
    const [total, setTotal] = useState(0);
    const [carregando, setCarregando] = useState(true);
    const [progresso, setProgresso] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [erro, setErro] = useState(false);
    const scrollRef = useRef(null);

    // URL proxiada via backend — resolve CORS no Android
    const urlParaUsar = musicaId
        ? (window.API_URL || "https://adorateam-api-532602191494.us-central1.run.app") + "/api/musicas/" + musicaId + "/partitura/view"
        : url;

    useEffect(() => {
        if (!urlParaUsar) return;
        setPaginas([]); setTotal(0); setCarregando(true); setProgresso(0); setErro(false);

        const token = localStorage.getItem("adorateam_token");

        const carregar = async () => {
            try {
                const pdfjsLib = window["pdfjs-dist/build/pdf"];
                pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

                const loadingTask = pdfjsLib.getDocument({
                    url: urlParaUsar,
                    httpHeaders: token ? { Authorization: "Bearer " + token } : {},
                    cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
                    cMapPacked: true,
                });
                const pdf = await loadingTask.promise;
                setTotal(pdf.numPages);

                // devicePixelRatio garante nitidez em telas retina (iPhone, Android AMOLED)
                const dpr = window.devicePixelRatio || 1;

                const imgs = [];
                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const baseViewport = page.getViewport({ scale: 1 });
                    // Escala para preencher largura da tela com alta resolução
                    const scale = (window.innerWidth / baseViewport.width) * dpr * 1.5;
                    const viewport = page.getViewport({ scale: Math.min(scale, 6) });
                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    const ctx2d = canvas.getContext("2d");
                    await page.render({ canvasContext: ctx2d, viewport }).promise;
                    imgs.push(canvas.toDataURL("image/jpeg", 0.95));
                    setProgresso(Math.round((i / pdf.numPages) * 100));
                }
                setPaginas(imgs);
                setCarregando(false);
            } catch (e) {
                console.error("LeitorPDF erro:", e);
                setCarregando(false);
                setErro(true);
            }
        };

        const iniciar = () => {
            if (window["pdfjs-dist/build/pdf"]) { carregar(); return; }
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            s.onload = carregar;
            s.onerror = () => { setCarregando(false); setErro(true); };
            document.head.appendChild(s);
        };
        iniciar();
    }, [urlParaUsar]);

    if (carregando) return (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, background: "#0a0f1e" }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", border: "3px solid #ffffff12", borderTop: "3px solid " + GOLD, animation: "spin 1s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            <div style={{ fontSize: 13, color: "#ffffff50" }}>Carregando PDF... {progresso}%</div>
            <div style={{ width: 160, height: 3, background: "#ffffff12", borderRadius: 2 }}>
                <div style={{ width: progresso + "%", height: "100%", background: GOLD, borderRadius: 2, transition: "width 0.3s" }} />
            </div>
        </div>
    );

    if (erro) return (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, background: "#0a0f1e" }}>
            <div style={{ fontSize: 36 }}>📄</div>
            <div style={{ fontSize: 14, color: "#ffffff60", textAlign: "center", maxWidth: 260 }}>
                Não foi possível renderizar o PDF. Tente abrir diretamente.
            </div>
            <a href={url} target="_blank" rel="noreferrer"
               style={{ background: GOLD, color: "#0f1729", borderRadius: 12, padding: "10px 24px", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
                Abrir PDF ↗
            </a>
        </div>
    );

    return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#111" }}>
            <div style={{ background: "#0a0f1e", borderBottom: "1px solid #ffffff10", padding: "6px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, color: "#ffffff40" }}>{total} página{total !== 1 ? "s" : ""}</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#ffffff12", color: "white", fontSize: 18, cursor: "pointer" }}>−</button>
                <span style={{ fontSize: 11, color: GOLD, fontWeight: 700 }}>{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#ffffff12", color: "white", fontSize: 18, cursor: "pointer" }}>+</button>
            </div>
            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", background: "#1a1a1a", padding: "10px 0" }}>
                {paginas.map((src, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                        <img src={src} alt={"Página " + (i + 1)} style={{ width: (zoom * 100) + "%", maxWidth: "100%", display: "block", borderRadius: 4, boxShadow: "0 2px 16px #0008" }} />
                    </div>
                ))}
                <div style={{ height: 40 }} />
            </div>
        </div>
    );
}

function ModoCulto({ cifra, partituraUrl, musicaId, onFechar, tema: T }) {
    const temCifra = !!cifra?.trim(), temPartitura = !!partituraUrl;
    const [velocidade, setVelocidade] = useState(2);
    const [rolando, setRolando] = useState(false);
    const [modo, setModo] = useState(!temCifra && temPartitura ? "partitura" : "cifra");
    const [fontSize, setFontSize] = useState(16);
    const containerRef = useRef(null), rafRef = useRef(null), lastTimeRef = useRef(null);
    const VEL_PX_S = [25, 50, 90, 150, 250];

    useEffect(() => {
        if (!rolando || modo !== "cifra") { cancelAnimationFrame(rafRef.current); lastTimeRef.current = null; return; }
        const loop = (ts) => {
            if (lastTimeRef.current === null) lastTimeRef.current = ts;
            const dt = (ts - lastTimeRef.current) / 1000; lastTimeRef.current = ts;
            if (containerRef.current) {
                containerRef.current.scrollTop += VEL_PX_S[velocidade - 1] * dt;
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                if (scrollTop + clientHeight >= scrollHeight - 10) { setRolando(false); return; }
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(rafRef.current); lastTimeRef.current = null; };
    }, [rolando, velocidade, modo]);

    // Renderiza linha de acordes — destaca cada acorde em dourado
    const renderLinha = (linha, i) => {
        if (!linha.trim()) return <div key={i} style={{ height: fontSize * 1.2 }} />;
        if (linha.startsWith("[")) {
            // Remove colchetes e exibe como label de seção
            return (
                <div key={i} style={{
                    color: GOLD, fontWeight: 800, fontSize: fontSize * 0.75,
                    textTransform: "uppercase", letterSpacing: 2,
                    marginTop: fontSize * 2, marginBottom: fontSize * 0.5,
                    borderLeft: "3px solid " + GOLD,
                    paddingLeft: 10, opacity: 0.9,
                }}>
                    {linha.replace(/[\[\]]/g, "")}
                </div>
            );
        }
        const tokens = linha.split(/(\s+)/);
        const ehLinhaAcordes = tokens.filter(t => t.trim()).every(t => isAcorde(t.trim()));
        if (ehLinhaAcordes && linha.trim()) {
            return (
                <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: "0 4px", marginBottom: fontSize * 0.3, minHeight: fontSize * 1.4 }}>
                    {tokens.map((t, j) =>
                        t.trim() && isAcorde(t.trim())
                            ? <span key={j} style={{ color: GOLD, fontWeight: 800, fontSize: fontSize, fontFamily: "monospace", letterSpacing: 0.5 }}>{t.trim()}</span>
                            : <span key={j} style={{ whiteSpace: "pre", fontSize: fontSize }}>{t}</span>
                    )}
                </div>
            );
        }
        return (
            <div key={i} style={{
                color: "rgba(255,255,255,0.9)", fontSize: fontSize,
                fontFamily: "monospace", whiteSpace: "pre",
                lineHeight: 1.5, marginBottom: fontSize * 0.15,
            }}>
                {linha || " "}
            </div>
        );
    };

    // Pré-processa: separa "[SECAO] Acordes" em duas linhas
    const linhasProcessadas = [];
    for (const l of (cifra || "").split("\n")) {
        const m = l.match(/^(\[[^\]]+\])\s+(.+)$/);
        if (m) { linhasProcessadas.push(m[1]); linhasProcessadas.push(m[2]); }
        else linhasProcessadas.push(l);
    }

    return createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "#060c1a", zIndex: 99999, display: "flex", flexDirection: "column" }}>
            {/* Barra de controles */}
            <div style={{ background: "#0a1020", borderBottom: "1px solid #ffffff12", padding: "8px 14px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {/* Cifra / Partitura */}
                {temPartitura && (
                    <div style={{ display: "flex", background: "#ffffff0d", borderRadius: 8, padding: 2, gap: 2 }}>
                        <button onClick={() => { setModo("cifra"); setRolando(false); }} style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: modo === "cifra" ? GOLD : "transparent", color: modo === "cifra" ? BLUE : "#ffffff70", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🎸 Cifra</button>
                        <button onClick={() => { setModo("partitura"); setRolando(false); }} style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: modo === "partitura" ? GOLD : "transparent", color: modo === "partitura" ? BLUE : "#ffffff70", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>📄 Partitura</button>
                    </div>
                )}

                {modo === "cifra" && temCifra && (
                    <>
                        {/* Tamanho da fonte */}
                        <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#ffffff0d", borderRadius: 8, padding: "2px 6px" }}>
                            <button onClick={() => setFontSize(s => Math.max(12, s - 2))} style={{ background: "none", border: "none", color: "#ffffff60", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "2px 4px" }}>A−</button>
                            <span style={{ fontSize: 10, color: "#ffffff40", minWidth: 24, textAlign: "center" }}>{fontSize}</span>
                            <button onClick={() => setFontSize(s => Math.min(28, s + 2))} style={{ background: "none", border: "none", color: "#ffffff60", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "2px 4px" }}>A+</button>
                        </div>
                        {/* Velocidade */}
                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {[1,2,3,4,5].map(v => (
                                <button key={v} onClick={() => setVelocidade(v)} style={{ width: 26, height: 26, borderRadius: 6, border: "none", background: velocidade === v ? GOLD : "#ffffff12", color: velocidade === v ? BLUE : "#ffffff50", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{v}</button>
                            ))}
                        </div>
                        {/* Play/Pause */}
                        <button onClick={() => setRolando(p => !p)} style={{ background: rolando ? "#ff4444" : GOLD, border: "none", borderRadius: 20, padding: "6px 18px", color: rolando ? "white" : BLUE, fontWeight: 800, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                            {rolando
                                ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> Pausar</>
                                : <><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg> Rolar</>
                            }
                        </button>
                    </>
                )}

                <button onClick={onFechar} style={{ background: "#ffffff12", border: "none", borderRadius: "50%", width: 32, height: 32, color: "white", fontSize: 18, cursor: "pointer", marginLeft: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            {/* Conteúdo da cifra */}
            {modo === "cifra" && (
                <div ref={containerRef} style={{ flex: 1, overflowY: "auto", padding: "28px 20px 120px", background: "#060c1a" }}>
                    <div style={{ maxWidth: 680, margin: "0 auto" }}>
                        {linhasProcessadas.map((linha, i) => renderLinha(linha, i))}
                    </div>
                </div>
            )}

            {/* Partitura */}
            {modo === "partitura" && partituraUrl && (
                partituraUrl.toLowerCase().split("?")[0].endsWith(".pdf")
                    ? <LeitorPDF url={partituraUrl} musicaId={musicaId} />
                    : <div style={{ flex: 1, overflowY: "auto", background: "#111", display: "flex", justifyContent: "center", padding: 16 }}>
                        <img src={partituraUrl} alt="Partitura" style={{ maxWidth: "100%", borderRadius: 8 }} />
                    </div>
            )}
        </div>,
        document.body
    );
}

function MusicaDetalhe({ musica, voltar, adminMode, setMusicas, tema }) {
    const T = TEMAS[tema];
    const [aba, setAba] = useState("cifra");
    const [semitons, setSemitons] = useState(0);
    const [instrumento, setInstrumento] = useState("violao");
    const [vozAtiva, setVozAtiva] = useState("Soprano");
    const [showCapo, setShowCapo] = useState(false);
    const [editando, setEditando] = useState(false);
    const [modoCulto, setModoCulto] = useState(false);
    const [loadingAcao, setLoadingAcao] = useState(false);
    const [capoAtivo, setCapoAtivo] = useState(0);
    const tomOriginal = musica.tom || "C";
    const cifraAtual = transporCifra(musica.cifra || "", semitons);
    const NOTAS_TOM = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
    const NOTAS_TOM_B = ["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"];
    const PREFERIR_B = ["F","Bb","Eb","Ab","Db","Gb"];
    const idxOrig = NOTAS_TOM.indexOf(tomOriginal) !== -1 ? NOTAS_TOM.indexOf(tomOriginal) : NOTAS_TOM_B.indexOf(tomOriginal);
    const idxAtual = ((idxOrig + semitons) % 12 + 12) % 12;
    const tomAtual = PREFERIR_B.includes(NOTAS_TOM[idxAtual]) ? NOTAS_TOM_B[idxAtual] : NOTAS_TOM[idxAtual];
    const sugestoesCapo = calcularCapotraste(tomOriginal, semitons);
    const handleSelectCapo = (casa) => {
        setCapoAtivo(casa);
        if (casa === 0) { setSemitons(0); return; }
        const sug = sugestoesCapo.find(s => s.capo === casa);
        if (!sug) return;
        const idxTocar = NOTAS_TOM.indexOf(sug.tomTocar) !== -1 ? NOTAS_TOM.indexOf(sug.tomTocar) : NOTAS_TOM_B.indexOf(sug.tomTocar);
        let diff = idxTocar - idxOrig;
        if (diff > 6) diff -= 12;
        if (diff < -6) diff += 12;
        setSemitons(diff);
    };
    const mudarParaTom = (novoTom) => {
        const idxOriginal = TONS.indexOf(tomOriginal), idxNovo = TONS.indexOf(novoTom);
        if (idxOriginal === -1 || idxNovo === -1) return;
        let diff = idxNovo - idxOriginal;
        if (diff > 6) diff -= 12;
        if (diff < -6) diff += 12;
        setSemitons(diff);
    };
    const uploadAudio = (voz, file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setMusicas(prev => prev.map(m => m.id !== musica.id ? m : { ...m, vozes: { ...m.vozes, [voz]: { ...m.vozes?.[voz], audioUrl: url } } }));
    };
    const uploadPartitura = async (file) => {
        if (!file) return;
        setLoadingAcao(true);
        try {
            const data = await musicasAPI.uploadPartitura(musica.id, file);
            const url = data.url || data.partituraUrl;
            setMusicas(prev => prev.map(m => m.id === musica.id
                ? { ...m, partituraUrl: url, partituranome: file.name }
                : m
            ));
            musica.partituraUrl = url;
            musica.partituranome = file.name;
        } catch (err) {
            alert("Erro ao enviar partitura: " + err.message);
        } finally {
            setLoadingAcao(false);
        }
    };
    const salvarEdicao = async (atualizado) => {
        setLoadingAcao(true);
        try {
            await musicasAPI.editar(musica.id, atualizado);
            setMusicas(prev => prev.map(m => m.id === musica.id ? { ...m, ...atualizado } : m));
            Object.assign(musica, atualizado);
        } catch (e) { alert("Erro ao salvar: " + e.message); }
        finally { setLoadingAcao(false); setEditando(false); }
    };
    const ABAS = [{ id: "cifra", label: "🎸 Cifra" }, { id: "partitura", label: "📄 Partitura" }, { id: "vocal", label: "🎤 Kit Vocal" }, { id: "audio", label: "▶ Vozes" }];
    return (
        <div style={{ padding: 16, paddingBottom: 80 }}>
            {modoCulto && <ModoCulto cifra={cifraAtual} partituraUrl={musica.partituraUrl} musicaId={musica.id} onFechar={() => setModoCulto(false)} tema={T} />}
            <button onClick={voltar} style={{ background: "none", border: "none", color: GOLD, fontSize: 14, cursor: "pointer", marginBottom: 12, padding: 0 }}>← Voltar</button>
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: T.texto }}>{musica.titulo}</div>
                {(musica.autor || musica.versao) && <div style={{ fontSize: 13, color: T.textoSecundario, marginTop: 2 }}>{musica.autor && <span>{musica.autor}</span>}{musica.versao && <span style={{ color: GOLD }}> · {musica.versao}</span>}</div>}
                <div style={{ fontSize: 13, color: T.textoSecundario, marginTop: 2 }}>Tom original: <span style={{ color: GOLD, fontWeight: 700 }}>{tomOriginal}</span></div>
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    {adminMode && <button onClick={() => setEditando(true)} style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}44`, borderRadius: 10, padding: "6px 14px", color: GOLD, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✏️ Editar</button>}
                    <button onClick={() => setModoCulto(true)} style={{ background: `linear-gradient(135deg, #7B2FBE, #9c27b0)`, border: "none", borderRadius: 10, padding: "6px 16px", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>⛪ Modo Culto</button>
                </div>
            </div>
            <VideoEnsino musica={musica} adminMode={adminMode} setMusicas={setMusicas} tema={T} />
            <div style={{ marginTop: 16, marginBottom: 16 }}>
                {musica.audioPrincipal && <Player src={musica.audioPrincipal} titulo={musica.titulo} subtitulo="Música completa" cor={GOLD} tema={T} />}
                {!musica.audioPrincipal && adminMode && <div style={{ background: T.bgCard, borderRadius: 16, padding: "14px 16px", border: `1px solid ${T.borda}`, textAlign: "center", color: T.textoSecundario, fontSize: 13 }}>Nenhum áudio carregado</div>}
                {adminMode && <label style={{ display: "block", marginTop: 8, background: T.bgCardSecundario, border: `1px solid ${T.borda}`, borderRadius: 10, padding: "8px 14px", fontSize: 12, color: GOLD, cursor: "pointer", fontWeight: 600, textAlign: "center" }}>📁 Upload da música completa<input type="file" accept="audio/*" style={{ display: "none" }} onChange={e => uploadAudio("audioPrincipal", e.target.files[0])} /></label>}
            </div>
            <div style={{ display: "flex", background: T.bgCard, borderRadius: 14, padding: 5, marginBottom: 18, border: `1px solid ${T.borda}`, overflowX: "auto", gap: 3 }}>
                {ABAS.map(({ id, label }) => <button key={id} onClick={() => setAba(id)} style={{ flexShrink: 0, flex: 1, padding: "9px 6px", border: "none", borderRadius: 10, background: aba === id ? GOLD : "transparent", color: aba === id ? BLUE : T.textoSecundario, fontWeight: aba === id ? 800 : 500, fontSize: 12, cursor: "pointer", transition: "all 0.15s", boxShadow: aba === id ? `0 2px 8px ${GOLD}44` : "none" }}>{label}</button>)}
            </div>
            {aba === "cifra" && (
                <div>
                    <div style={{ background: T.bgCard, borderRadius: 14, padding: "12px 14px", marginBottom: 12, border: `1px solid ${T.borda}` }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.texto }}>Tom: <span style={{ color: GOLD }}>{tomOriginal}</span>{semitons !== 0 && <span> → <span style={{ color: GOLD2, fontWeight: 700 }}>{tomAtual}</span></span>}</div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <button onClick={() => { setSemitons(p => p - 1); setCapoAtivo(0); }} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.borda}`, background: T.bgCardSecundario, color: T.texto, fontSize: 16, cursor: "pointer", fontWeight: 700 }}>−</button>
                                <span style={{ fontSize: 13, color: T.textoSecundario, minWidth: 30, textAlign: "center" }}>{semitons > 0 ? `+${semitons}` : semitons}</span>
                                <button onClick={() => { setSemitons(p => p + 1); setCapoAtivo(0); }} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.borda}`, background: T.bgCardSecundario, color: T.texto, fontSize: 16, cursor: "pointer", fontWeight: 700 }}>+</button>
                                <button onClick={() => { setSemitons(0); setCapoAtivo(0); }} style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${T.borda}`, background: T.bgCardSecundario, fontSize: 12, cursor: "pointer", color: T.textoSecundario }}>Reset</button>
                            </div>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, color: T.textoSecundario, marginBottom: 6 }}>Ir direto para o tom:</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {TONS.map(t => <button key={t} onClick={() => mudarParaTom(t)} style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${tomAtual === t ? GOLD : T.borda}`, background: tomAtual === t ? GOLD : T.bgCardSecundario, color: tomAtual === t ? BLUE : T.texto, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t}</button>)}
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                            {[["violao", "🎸 Violão"], ["piano", "🎹 Piano"]].map(([id, label]) => <button key={id} onClick={() => setInstrumento(id)} style={{ padding: "4px 12px", borderRadius: 8, border: `1px solid ${instrumento === id ? GOLD : T.borda}`, background: instrumento === id ? GOLD : T.bgCardSecundario, color: instrumento === id ? BLUE : T.texto, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>)}
                        </div>
                        <button onClick={() => setShowCapo(!showCapo)} style={{ fontSize: 12, background: "none", border: `1px solid ${T.borda}`, borderRadius: 8, padding: "4px 12px", cursor: "pointer", color: T.textoSecundario }}>🎸 {showCapo ? "Ocultar" : "Ver"} sugestões de capotraste</button>
                        {showCapo && <SeletorCapo sugestoes={sugestoesCapo} capoAtivo={capoAtivo} onSelectCapo={handleSelectCapo} tema={T} />}
                    </div>
                    <div style={{ background: T.bgCard, borderRadius: 14, padding: 16, border: `1px solid ${T.borda}` }}><CifraFormatada cifra={cifraAtual} instrumento={instrumento} tema={T} /></div>
                </div>
            )}
            {aba === "partitura" && (
                musica.partituraUrl ? (
                    <div>
                        {(() => {
                            const nome = (musica.partituranome || musica.partituraUrl || "").toLowerCase();
                            const isPDF = nome.endsWith(".pdf");
                            if (isPDF) {
                                return <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${T.borda}`, marginBottom: 12, height: "72vh", display: "flex", flexDirection: "column" }}><LeitorPDF url={musica.partituraUrl} musicaId={musica.id} /></div>;
                            }
                            return (
                                <div style={{ background: T.bgCard, borderRadius: 14, overflow: "hidden", border: `1px solid ${T.borda}`, marginBottom: 12 }}>
                                    <img src={musica.partituraUrl} alt="Partitura" style={{ width: "100%", display: "block" }} />
                                </div>
                            );
                        })()}
                        {adminMode && <label style={{ display: "block", background: T.bgCardSecundario, border: `1px solid ${T.borda}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: GOLD, cursor: loadingAcao ? "default" : "pointer", fontWeight: 600, textAlign: "center", opacity: loadingAcao ? 0.6 : 1 }}>{loadingAcao ? "⏳ Enviando..." : "🔄 Trocar partitura"}<input type="file" accept="image/*,application/pdf" style={{ display: "none" }} disabled={loadingAcao} onChange={e => uploadPartitura(e.target.files[0])} /></label>}
                    </div>
                ) : (
                    <div style={{ background: T.bgCard, borderRadius: 14, padding: 30, border: `1px solid ${T.borda}`, textAlign: "center" }}>
                        <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.texto, marginBottom: 4 }}>Nenhuma partitura ainda</div>
                        {adminMode && <label style={{ display: "inline-block", background: loadingAcao ? "#ccc" : GOLD, borderRadius: 10, padding: "10px 20px", fontSize: 13, color: BLUE, cursor: loadingAcao ? "default" : "pointer", fontWeight: 700, opacity: loadingAcao ? 0.7 : 1 }}>{loadingAcao ? "⏳ Enviando..." : "📎 Anexar partitura"}<input type="file" accept="image/*,application/pdf" style={{ display: "none" }} disabled={loadingAcao} onChange={e => uploadPartitura(e.target.files[0])} /></label>}
                    </div>
                )
            )}
            {aba === "vocal" && (
                <div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 14, justifyContent: "center", flexWrap: "wrap" }}>
                        {Object.keys(VOZ_CORES).map(voz => <button key={voz} onClick={() => setVozAtiva(voz)} style={{ padding: "6px 14px", borderRadius: 20, border: "none", background: vozAtiva === voz ? VOZ_CORES[voz].cor : T.bgCardSecundario, color: vozAtiva === voz ? "white" : T.textoSecundario, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{voz}</button>)}
                    </div>
                    {(() => {
                        const dados = musica.vozes?.[vozAtiva];
                        const c = VOZ_CORES[vozAtiva];
                        if (!dados) return null;
                        return (
                            <div style={{ background: T.bgCard, borderRadius: 16, padding: 20, border: `1px solid ${T.borda}` }}>
                                <Player src={dados.audioUrl} titulo={vozAtiva} subtitulo={musica.titulo} cor={c.cor} tema={T} />
                                {adminMode && <label style={{ display: "block", marginTop: 10, background: T.bgCardSecundario, border: `1px solid ${T.borda}`, borderRadius: 10, padding: "8px 14px", fontSize: 12, color: c.cor, cursor: "pointer", fontWeight: 600, textAlign: "center" }}>📁 Upload — {vozAtiva}<input type="file" accept="audio/*" style={{ display: "none" }} onChange={e => uploadAudio(vozAtiva, e.target.files[0])} /></label>}
                            </div>
                        );
                    })()}
                </div>
            )}
            {aba === "audio" && Object.entries(musica.vozes || {}).map(([voz, dados]) => {
                const c = VOZ_CORES[voz] || { cor: "#666" };
                return <div key={voz} style={{ marginBottom: 12 }}><Player src={dados.audioUrl} titulo={voz} subtitulo={musica.titulo} cor={c.cor} tema={T} /></div>;
            })}
            {editando && <EditarCifra musica={musica} tema={T} onCancelar={() => setEditando(false)} onSalvar={salvarEdicao} />}
        </div>
    );
}

function Sugestoes({ sugestoes, adicionarSugestao, setSugestoes, adminMode, nomeUsuario, tema }) {
    const T = TEMAS[tema];
    const [titulo, setTitulo] = useState(""), [artista, setArtista] = useState("");
    const enviar = () => {
        if (!titulo) return;
        adicionarSugestao({ id: Date.now(), titulo, artista, autor: nomeUsuario, votos: 0, status: "pendente" });
        setTitulo(""); setArtista("");
    };
    return (
        <div style={{ padding: 16, paddingBottom: 80 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.texto, marginBottom: 16 }}>💡 Sugestões</div>
            <div style={{ background: T.bgCard, borderRadius: 16, padding: 16, marginBottom: 16, border: `1px solid ${T.borda}` }}>
                <input placeholder="Nome da música" value={titulo} onChange={e => setTitulo(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${T.borda}`, background: T.input, color: T.texto, fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} />
                <input placeholder="Artista (opcional)" value={artista} onChange={e => setArtista(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${T.borda}`, background: T.input, color: T.texto, fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} />
                <button onClick={enviar} style={{ width: "100%", padding: 12, borderRadius: 10, background: GOLD, border: "none", color: BLUE, fontWeight: 700, cursor: "pointer" }}>Sugerir 💡</button>
            </div>
            {adminMode && sugestoes.map(s => (
                <div key={s.id} style={{ background: T.bgCard, borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${T.borda}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                            <div style={{ fontWeight: 700, color: T.texto }}>{s.titulo}</div>
                            {s.artista && <div style={{ fontSize: 12, color: T.textoSecundario }}>{s.artista}</div>}
                            <div style={{ fontSize: 11, color: T.textoSecundario }}>por {s.autor}</div>
                        </div>
                        <div style={{ display: "flex", gap: 5 }}>
                            {s.status === "pendente" && (<><button onClick={() => setSugestoes(prev => prev.map(x => x.id === s.id ? { ...x, status: "aprovada" } : x))} style={{ background: "#4caf5022", border: "1px solid #4caf5044", borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 12, color: "#4caf50", fontWeight: 700 }}>✓</button><button onClick={() => setSugestoes(prev => prev.map(x => x.id === s.id ? { ...x, status: "rejeitada" } : x))} style={{ background: "#f4433618", border: "1px solid #f4433630", borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 12, color: "#f44336", fontWeight: 700 }}>✗</button></>)}
                            <button onClick={() => setSugestoes(prev => prev.filter(x => x.id !== s.id))} style={{ background: "#fce4ec", border: "none", borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 13 }}>🗑</button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function AfinadorTab({ tema, ativo }) {
    const T = TEMAS[tema];
    if (!ativo) return null;
    return <div style={{ padding: 16, paddingBottom: 80 }}><Afinador tema={T} abaAberta={true} /></div>;
}


// ── Modal de Planos ───────────────────────────────────────────────────────────
function ModalPlanos({ planoAtual, uso, onFechar, tema: T }) {
    const PLANOS = [
        {
            id: "starter", nome: "Starter", emoji: "🌱", cor: "#4CAF50",
            preco: "Grátis", periodo: "para sempre", maxMembros: 10,
            recursos: ["Gestão de membros", "Cifras e repertório", "Escala e afinador", "Até 10 membros"],
        },
        {
            id: "pro", nome: "Pro", emoji: "⚡", cor: "#F9A826", destaque: true,
            preco: "R$ 49,90", periodo: "/mês", maxMembros: 30,
            recursos: ["Tudo do Starter", "Filtros avançados", "Histórico de atividades", "Até 30 membros"],
        },
        {
            id: "premium", nome: "Premium", emoji: "👑", cor: "#9C27B0",
            preco: "R$ 89,90", periodo: "/mês", maxMembros: null,
            recursos: ["Tudo do Pro", "Relatórios da equipe", "Suporte prioritário", "Membros ilimitados"],
        },
    ];

    return (
        <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 99999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(6px)" }}>
            <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
            <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 430, background: T.bgCard, borderRadius: "24px 24px 0 0", padding: "24px 20px 32px", maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box", animation: "slideUp 0.3s ease" }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: T.texto }}>Escolher Plano</div>
                        <div style={{ fontSize: 12, color: T.textoSecundario, marginTop: 2 }}>
                            Plano atual: <span style={{ color: GOLD, fontWeight: 700 }}>{planoAtual.charAt(0).toUpperCase() + planoAtual.slice(1)}</span>
                            {uso && !uso.ilimitado && ` · ${uso.total_membros}/${uso.limite} membros`}
                        </div>
                    </div>
                    <button onClick={onFechar} style={{ background: T.bgCardSecundario, border: "none", borderRadius: "50%", width: 34, height: 34, fontSize: 18, cursor: "pointer", color: T.texto, flexShrink: 0 }}>✕</button>
                </div>

                {/* Cards dos planos */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {PLANOS.map(p => {
                        const ativo = planoAtual === p.id;
                        return (
                            <div key={p.id} style={{
                                borderRadius: 18, padding: "16px 18px",
                                border: `2px solid ${ativo ? p.cor : p.destaque ? p.cor + "44" : T.borda}`,
                                background: ativo ? p.cor + "12" : p.destaque ? p.cor + "08" : T.bgCardSecundario,
                                position: "relative", overflow: "hidden",
                            }}>
                                {p.destaque && !ativo && (
                                    <div style={{ position: "absolute", top: 12, right: 12, background: p.cor, borderRadius: 10, padding: "2px 10px", fontSize: 10, fontWeight: 800, color: BLUE }}>
                                        POPULAR
                                    </div>
                                )}
                                {ativo && (
                                    <div style={{ position: "absolute", top: 12, right: 12, background: p.cor, borderRadius: 10, padding: "2px 10px", fontSize: 10, fontWeight: 800, color: "white" }}>
                                        ATUAL
                                    </div>
                                )}

                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                    <span style={{ fontSize: 24 }}>{p.emoji}</span>
                                    <div>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: T.texto }}>{p.nome}</div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: p.cor }}>
                                            {p.preco} <span style={{ fontSize: 11, fontWeight: 400, color: T.textoSecundario }}>{p.periodo}</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 }}>
                                    {p.recursos.map((r, i) => (
                                        <div key={i} style={{ fontSize: 12, color: T.textoSecundario, display: "flex", alignItems: "center", gap: 6 }}>
                                            <span style={{ color: p.cor, fontSize: 10 }}>✓</span> {r}
                                        </div>
                                    ))}
                                </div>

                                {!ativo && (
                                    <button
                                        onClick={() => window.open("https://adorateam.com.br/planos", "_blank")}
                                        style={{
                                            width: "100%", padding: "10px 0", borderRadius: 12,
                                            background: p.destaque ? `linear-gradient(135deg, ${p.cor}, ${p.cor}cc)` : "transparent",
                                            border: `1.5px solid ${p.cor}`,
                                            color: p.destaque ? BLUE : p.cor,
                                            fontWeight: 800, fontSize: 13, cursor: "pointer",
                                        }}
                                    >
                                        {planoAtual === "starter" || (planoAtual === "pro" && p.id === "premium") ? "Assinar agora" : "Fazer downgrade"}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: T.textoSecundario }}>
                    Dúvidas? Fale conosco em suporte@adorateam.com.br
                </div>
            </div>
        </div>
    );
}

function Carregando({ tema }) {
    const T = TEMAS[tema];
    return (
        <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
            <LogoAdoraTeam size={60} glow={true} />
            <div style={{ fontSize: 14, color: T.textoSecundario }}>Carregando...</div>
        </div>
    );
}

// ────────────────────────────────────────────
// APP PRINCIPAL
// ────────────────────────────────────────────
export default function App() {
    const [splash, setSplash] = useState(true);
    const [tema, setTema] = useState(() => lerLS("tema", "escuro"));
    const [usuario, setUsuario] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [telaAuth, setTelaAuth] = useState("boasvindas");
    const [novoUsuario, setNovoUsuario] = useState(null);
    const [painelLider, setPainelLider] = useState(false);
    const [tab, setTab] = useState("home");
    const [adminMode, setAdminMode] = useState(false);
    const [musicas, setMusicas] = useState([]);
    const [musicaAberta, setMusicaAberta] = useState(null);
    const [escala, setEscala] = useState([]);
    const [sugestoes, setSugestoes] = useState(() => lerLS("sugestoes", []));
    const [atividades, setAtividades] = useState(() => lerLS("atividades", []));
    const [membros, setMembros] = useState([]);
    const [grupo, setGrupo] = useState(null);
    const [showPlanos, setShowPlanos] = useState(false);
    const T = TEMAS[tema];

    const API_URL = process.env.REACT_APP_API_URL || "https://adorateam-api-532602191494.us-central1.run.app";

    const carregarDados = useCallback(async () => {
        try {
            const [mscs, evts] = await Promise.all([
                musicasAPI.listar().catch(() => []),
                eventosAPI.listar().catch(() => []),
            ]);
            setMusicas(mscs.map(normalizarMusica));
            // ── CORREÇÃO CRÍTICA: normaliza foto_look → fotoPaleta ──
            // O banco retorna foto_look mas o frontend usa fotoPaleta
            setEscala(evts.map(normalizarEvento));
        } catch (e) {
            console.error("Erro ao carregar dados:", e);
        }

        try {
            const token = localStorage.getItem("adorateam_token");
            const res = await fetch(`${API_URL}/api/grupos/meu`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const lista = data.membros || data.members || data || [];
            if (Array.isArray(lista)) {
                // Normaliza foto: pode vir como foto, foto_url ou avatar do banco
                setMembros(lista.map(m => ({
                    ...m,
                    foto: m.foto || m.foto_url || m.avatar || m.picture || null,
                })));
            }
            setGrupo(data);
        } catch (e) {
            console.error("Erro ao carregar membros:", e);
        }
    }, [API_URL]);

    useEffect(() => {
        const verificarToken = async () => {
            const token = localStorage.getItem("adorateam_token");
            if (!token) { setCarregando(false); return; }
            try {
                const res = await auth.me();
                setUsuario(res);
                await carregarDados();
            } catch { removerToken(); }
            finally { setCarregando(false); }
        };
        verificarToken();
    }, []);

    const handleLogin = async (usuarioData) => {
        setUsuario(usuarioData);
        setTelaAuth(null);
        setNovoUsuario(null);
        await carregarDados();
    };

    const handleLogout = () => {
        removerToken();
        setUsuario(null); setMusicas([]); setEscala([]); setMembros([]);
        setAdminMode(false); setTelaAuth("boasvindas");
    };

    useEffect(() => { salvarLS("tema", tema); }, [tema]);
    useEffect(() => { salvarLS("sugestoes", sugestoes); }, [sugestoes]);
    useEffect(() => { salvarLS("atividades", atividades); }, [atividades]);

    const adicionarAtividade = (icon, texto) => {
        setAtividades(prev => [{ icon, texto, tempo: "agora", id: Date.now() }, ...prev].slice(0, 20));
    };

    const temPermissaoAdmin = usuario?.role === "lider" ||
        usuario?.permissoes?.can_manage_repertoire ||
        usuario?.permissoes?.can_manage_schedule ||
        usuario?.permissoes?.can_add_songs ||
        usuario?.permissoes?.can_edit_songs;

    useEffect(() => { if (usuario) setAdminMode(temPermissaoAdmin); }, [usuario]);

    const abrirMusica = (m) => { setMusicaAberta(m); setTab("cifra"); };
    const voltarMusicas = () => { setMusicaAberta(null); setTab("musicas"); };

    const adicionarMusica = async (nova) => {
        try {
            await musicasAPI.criar(nova);
            const lista = await musicasAPI.listar();
            setMusicas(lista);
            adicionarAtividade("🎵", `Música "${nova.titulo}" adicionada`);
        } catch (e) { alert("Erro ao adicionar música: " + e.message); }
    };

    const adicionarSugestao = (s) => {
        setSugestoes(prev => [s, ...prev]);
        adicionarAtividade("💡", `Sugestão "${s.titulo}" enviada`);
    };

    const proximoEvento = escala.find(e => new Date(e.data) >= new Date());
    const [dropdownAberto, setDropdownAberto] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!dropdownAberto) return;
        const fechar = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownAberto(false); };
        document.addEventListener("mousedown", fechar);
        return () => document.removeEventListener("mousedown", fechar);
    }, [dropdownAberto]);

    if (splash) return <SplashScreen onFim={() => setSplash(false)} />;
    if (carregando) return <Carregando tema={tema} />;

    if (!usuario) {
        if (telaAuth === "boasvindas") return <TelaBoasVindas onCriarGrupo={() => setTelaAuth("login")} onEntrarGrupo={() => setTelaAuth("entrar-codigo")} />;
        if (telaAuth === "login") return <Login onLogin={handleLogin} onCadastro={() => setTelaAuth("cadastro-lider")} onVoltar={() => setTelaAuth("boasvindas")} />;
        if (telaAuth === "cadastro-lider") return <Cadastro onLogin={handleLogin} onVoltar={() => setTelaAuth("login")} onEscolherPlano={(u) => { setNovoUsuario(u); setTelaAuth("escolher-plano"); }} />;
        if (telaAuth === "entrar-codigo") return <EntrarComCodigo onLogin={handleLogin} onVoltar={() => setTelaAuth("boasvindas")} />;
        if (telaAuth === "escolher-plano") return <EscolherPlano usuario={novoUsuario} onConcluir={handleLogin} />;
    }

    if (painelLider) return <PainelLider usuario={usuario} onVoltar={() => setPainelLider(false)} />;

    return (
        <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: T.bg }}>
            {/* Header */}
            <div style={{ position: "sticky", top: 0, zIndex: 50, background: T.nav, borderBottom: `1px solid ${T.borda}`, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <LogoAdoraTeam size={35} />
                    <span style={{ fontWeight: 800, fontSize: 18, color: T.texto }}>AdoraTeam</span>
                </div>
                <div ref={dropdownRef} style={{ position: "relative" }}>
                    <button onClick={() => setDropdownAberto(p => !p)} style={{ background: dropdownAberto ? `${GOLD}18` : T.bgCard, border: `1.5px solid ${dropdownAberto ? GOLD + "88" : T.borda}`, borderRadius: 12, width: 40, height: 40, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, cursor: "pointer", padding: 0, boxShadow: dropdownAberto ? `0 0 12px ${GOLD}33` : "none", transition: "all 0.2s ease" }}>
                        {[0,1,2].map(i => <div key={i} style={{ height: 2, borderRadius: 2, background: dropdownAberto ? GOLD : T.texto, transition: "all 0.25s ease", width: i === 1 ? 14 : 20, opacity: dropdownAberto && i === 1 ? 0 : 1, transform: dropdownAberto ? i === 0 ? "translateY(7px) rotate(45deg)" : i === 2 ? "translateY(-7px) rotate(-45deg)" : "none" : "none" }} />)}
                    </button>
                    {dropdownAberto && (
                        <div style={{ position: "absolute", top: 46, right: 0, width: 200, background: T.bgCard, border: `1px solid ${T.borda}`, borderRadius: 16, boxShadow: "0 8px 32px #0008", overflow: "hidden", zIndex: 200, animation: "fadeUp 0.15s ease" }}>
                            {/* Info usuário + plano */}
                            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.borda}` }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.texto }}>{usuario?.nome}</div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                                    <div style={{ fontSize: 11, color: T.textoSecundario }}>{usuario?.role === "lider" ? "👑 Líder" : "🎵 Membro"}</div>
                                    {grupo?.plano && (
                                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                                            background: grupo.plano === "premium" ? "#9c27b022" : grupo.plano === "pro" ? `${GOLD}22` : "#ffffff12",
                                            color: grupo.plano === "premium" ? "#ce93d8" : grupo.plano === "pro" ? GOLD : T.textoSecundario,
                                            border: `1px solid ${grupo.plano === "premium" ? "#9c27b055" : grupo.plano === "pro" ? GOLD + "44" : T.borda}`,
                                        }}>
                                            {grupo.plano === "premium" ? "👑 Premium" : grupo.plano === "pro" ? "⚡ Pro" : "🌱 Starter"}
                                        </span>
                                    )}
                                </div>
                                {/* Barra de membros */}
                                {grupo?.uso && !grupo.uso.ilimitado && (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                            <span style={{ fontSize: 10, color: T.textoSecundario }}>👥 Membros</span>
                                            <span style={{ fontSize: 10, color: grupo.uso.limite_atingido ? "#f44336" : T.textoSecundario, fontWeight: 600 }}>
                                                {grupo.uso.total_membros}/{grupo.uso.limite}
                                            </span>
                                        </div>
                                        <div style={{ height: 3, background: T.borda, borderRadius: 2 }}>
                                            <div style={{ width: `${Math.min(grupo.uso.pct, 100)}%`, height: "100%", borderRadius: 2,
                                                background: grupo.uso.limite_atingido ? "#f44336" : grupo.uso.pct > 80 ? "#FF9800" : GOLD,
                                                transition: "width 0.3s" }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Upgrade de plano — só líder */}
                            {usuario?.role === "lider" && grupo?.plano !== "premium" && (
                                <button onClick={() => { setShowPlanos(true); setDropdownAberto(false); }} style={{ width: "100%", padding: "12px 16px", border: "none", background: `linear-gradient(135deg, ${GOLD}15, ${GOLD}08)`, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${T.borda}`, textAlign: "left" }}>
                                    <span style={{ fontSize: 16 }}>🚀</span>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: GOLD }}>Fazer upgrade</div>
                                        <div style={{ fontSize: 10, color: T.textoSecundario }}>{grupo?.plano === "pro" ? "Ir para Premium" : "Ver planos"}</div>
                                    </div>
                                </button>
                            )}
                            {usuario?.role === "lider" && <button onClick={() => { setPainelLider(true); setDropdownAberto(false); }} style={{ width: "100%", padding: "12px 16px", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${T.borda}`, textAlign: "left" }}><span style={{ fontSize: 16 }}>👑</span><span style={{ fontSize: 13, fontWeight: 600, color: GOLD }}>Painel do Líder</span></button>}
                            <button onClick={() => { setTema(t => t === "escuro" ? "claro" : "escuro"); setDropdownAberto(false); }} style={{ width: "100%", padding: "12px 16px", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${T.borda}`, textAlign: "left" }}><span style={{ fontSize: 16 }}>{tema === "escuro" ? "☀️" : "🌙"}</span><span style={{ fontSize: 13, fontWeight: 600, color: T.texto }}>{tema === "escuro" ? "Tema claro" : "Tema escuro"}</span></button>
                            <button onClick={() => { handleLogout(); setDropdownAberto(false); }} style={{ width: "100%", padding: "12px 16px", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}><span style={{ fontSize: 16 }}>🚪</span><span style={{ fontSize: 13, fontWeight: 600, color: "#ff4444" }}>Sair</span></button>
                        </div>
                    )}
                </div>
            </div>

            {/* Conteúdo */}
            <div>
                {tab === "home" && !musicaAberta && <Home musicas={musicas} abrirMusica={abrirMusica} setTab={setTab} perfil={usuario} proximoEvento={proximoEvento} atividades={atividades} setAtividades={setAtividades} atualizarFoto={foto => setUsuario(p => ({ ...p, foto }))} tema={tema} />}
                {tab === "escala" && (
                    <Escala
                        escala={escala} setEscala={setEscala}
                        musicas={musicas} membros={membros} setMembros={setMembros}
                        adminMode={adminMode} perfilNome={usuario?.nome}
                        onSalvarSetlist={async (id, setlist) => { await eventosAPI.atualizarSetlist(id, setlist); }}
                        tema={tema}
                    />
                )}
                {(tab === "musicas" || tab === "cifra") && !musicaAberta && <Musicas musicas={musicas} adicionarMusica={adicionarMusica} adminMode={adminMode} abrirMusica={abrirMusica} tema={tema} />}
                {musicaAberta && <MusicaDetalhe musica={musicaAberta} voltar={voltarMusicas} adminMode={adminMode} setMusicas={setMusicas} tema={tema} />}
                {tab === "afinador" && !musicaAberta && <AfinadorTab tema={tema} ativo={true} />}
                {tab === "rede" && !musicaAberta && <RedeSocial tema={T} perfil={usuario} membros={membros} adminMode={adminMode} />}
                {tab === "sugestoes" && !musicaAberta && <Sugestoes sugestoes={sugestoes} adicionarSugestao={adicionarSugestao} setSugestoes={setSugestoes} adminMode={adminMode} nomeUsuario={usuario?.nome} tema={tema} />}
                {tab === "perfil" && !musicaAberta && <Perfil usuario={usuario} onAtualizar={(u) => setUsuario(u)} tema={T} />}
                {tab === "chat" && !musicaAberta && <Chat usuario={usuario} tema={T} />}
                {tab === "equipe" && !musicaAberta && <RedeSocial tema={T} perfil={usuario} membros={membros} adminMode={adminMode} />}
            </div>

            {/* Navbar */}
            <Navbar tab={tab} setTab={t => { setMusicaAberta(null); setTab(t); }} T={T} />

            {/* Modal de planos */}
            {showPlanos && (
                <ModalPlanos
                    planoAtual={grupo?.plano || "starter"}
                    uso={grupo?.uso}
                    onFechar={() => setShowPlanos(false)}
                    tema={T}
                />
            )}
        </div>
    );
}